import {
  Layers,
  Coins,
  Wallet as WalletIcon,
  CreditCard,
  Landmark,
  ArrowLeftRight,
  Store,
  Send,
  Plug,
} from "lucide-react";
import {
  PageHero,
  Container,
  SectionHead,
  SplitSection,
  FeatureGrid,
  CTASection,
  PrimaryButton,
} from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import WalletMock from "@/components/visuals/WalletMock";
import ArchitectureDiagram from "@/components/visuals/ArchitectureDiagram";

const FEATURES = [
  {
    icon: <Layers className="h-5 w-5" />,
    title: "Multi-tenant architecture",
    desc: "Operate multiple programs, brands and partners on isolated tenants within one platform.",
  },
  {
    icon: <Coins className="h-5 w-5" />,
    title: "Multi-currency",
    desc: "Hold, convert and move balances across currencies with ledger-accurate accounting.",
  },
  {
    icon: <WalletIcon className="h-5 w-5" />,
    title: "Wallet management",
    desc: "Lifecycle, limits, holds and transaction controls for consumer and business wallets.",
  },
  {
    icon: <CreditCard className="h-5 w-5" />,
    title: "Card issuance",
    desc: "Issue virtual and physical cards directly against wallet balances.",
  },
  {
    icon: <Landmark className="h-5 w-5" />,
    title: "ACH & Wire",
    desc: "Move money in and out through bank rails with full status visibility.",
  },
  {
    icon: <ArrowLeftRight className="h-5 w-5" />,
    title: "P2P transfers",
    desc: "Instant ledger-backed peer-to-peer transfers between wallets.",
  },
  {
    icon: <Store className="h-5 w-5" />,
    title: "Merchant portal",
    desc: "Self-serve portals for merchants and partners with reporting and settlement views.",
  },
  {
    icon: <Send className="h-5 w-5" />,
    title: "Disbursements",
    desc: "Batch and real-time payouts to bank accounts, cards and wallets.",
  },
  {
    icon: <Plug className="h-5 w-5" />,
    title: "60+ connectors",
    desc: "Pre-built integrations for banks, processors, KYC, FX and financial services.",
  },
];

export default function Wallet() {
  return (
    <>
      <PageHero
        eyebrow="Wallet Platform"
        title="Multi-tenant issuer infrastructure."
        copy="A configurable wallet and issuer platform designed for multi-currency, multi-tenant financial products, from stored value and P2P to card issuance and disbursements."
        testid="wallet-page-title"
      >
        <Reveal delay={120} className="mt-9 flex flex-col gap-4 sm:flex-row">
          <PrimaryButton to="/contact" testid="wallet-hero-cta">
            Talk to Our Payments Team
          </PrimaryButton>
        </Reveal>
      </PageHero>

      <section className="py-20 lg:py-24">
        <Container>
          <SplitSection
            eyebrow="Product Overview"
            title="Accounts, balances and money movement in one stack."
            copy="The iJeeva Wallet Platform gives fintechs and enterprises the core infrastructure to issue wallets, hold multi-currency balances, move money across rails and operate programs at scale."
            points={[
              "Multi-currency wallets with ledger accuracy",
              "Card issuance against wallet balances",
              "ACH, Wire and P2P money movement",
              "Merchant and partner portals",
              "Batch and real-time disbursements",
              "60+ pre-built financial connectors",
            ]}
            visual={<WalletMock />}
          />
          <p className="mt-6 text-xs text-[#94A3B8]">Dashboard shown with representative demo data.</p>
        </Container>
      </section>

      <section className="border-y border-[#E2E8F0] bg-white py-20 lg:py-24">
        <Container>
          <SectionHead
            eyebrow="Capabilities"
            title="Everything an issuer needs to operate."
            copy="Configurable building blocks for wallet-led financial products."
          />
          <div className="mt-14">
            <FeatureGrid items={FEATURES} testidPrefix="wallet-feature" />
          </div>
        </Container>
      </section>

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead
            center
            eyebrow="Architecture"
            title="Ledger-first. Event-driven. Extensible."
            copy="The wallet stack runs on iJeeva's reference architecture: API-first services with an extensible integration layer."
          />
          <div className="mt-14">
            <ArchitectureDiagram />
          </div>
        </Container>
      </section>

      <CTASection
        title="Building a wallet-led financial product?"
        copy="Tell us about your program, and we'll show you how the platform maps to your requirements."
      />
    </>
  );
}
